import { HttpParams } from '@angular/common/http';
import { PagedRequest } from '../models/paged-request.model';
import { DateTime } from 'luxon';
import { catchError, Observable } from 'rxjs';

export const isMobile = {
  Android: (): boolean => !!navigator.userAgent.match(/Android/i),
  BlackBerry: (): boolean => !!navigator.userAgent.match(/BlackBerry/i),
  iOS: (): boolean => !!navigator.userAgent.match(/iPhone|iPad|iPod/i),
  Opera: (): boolean => !!navigator.userAgent.match(/Opera Mini/i),
  Windows: (): boolean => !!navigator.userAgent.match(/IEMobile/i) || !!navigator.userAgent.match(/WPDesktop/i),
  // eslint-disable-next-line id-denylist
  any: (): boolean => (isMobile.Android() || isMobile.BlackBerry() || isMobile.iOS() || isMobile.Opera() || isMobile.Windows())
};

export function isSafari(): boolean {
  return !!navigator.vendor && navigator.vendor.includes('Apple') &&
    !!navigator.userAgent &&
    navigator.userAgent.includes('CriOS') &&
    navigator.userAgent.includes('FxiOS');
}

export function isRobot(): boolean {
  const userAgent = navigator.userAgent;
  return /bot|crawler|spider|crawling/i.test(userAgent);
}

export function isNullOrEmpty(value: string | number): boolean {
  const val = value;
  return !val || (typeof val === 'string' && val.trim() === ''); // || (typeof value === 'number' && value );
}

/*
export function logInvalidFormFields(parent: FormGroup | FormArray, detailed: boolean = false, combinedKey: string = ''): void {
  for (const key of Object.keys(parent.controls)) {
    const control = parent.controls[key];
    if (control instanceof FormArray) {
      if (control.invalid) {
        console.log(`FormArray [${combinedKey}${key}] is invalid`, detailed ? control.value : null, detailed ? control.errors : null);
      }
      logInvalidFormFields(control, detailed, `${combinedKey}${key}.`);
    } else if (control instanceof FormGroup) {
      if (control.invalid) {
        console.log(`FormGroup [${combinedKey}${key}] is invalid`, detailed ? control.value : null, detailed ? control.errors : null);
      }
      logInvalidFormFields(control, detailed, `${combinedKey}${key}.`);
    } else if (control.invalid) {
      console.log(`Control [${combinedKey}${key}] is invalid`, detailed ? control.value : null, detailed ? control.errors : null);
    }
  }
}
*/

/**
 * @param {Document} doc - pass injected Document when executing in SSR
 * @returns {number}
 */
export function getMaxBrowserWidth(doc: Document = document): number {
  return Math.max(
    doc.body.scrollWidth,
    doc.documentElement.scrollWidth,
    doc.body.offsetWidth,
    doc.documentElement.offsetWidth,
    doc.documentElement.clientWidth
  );
}

/**
 * Simple object check.
 * @param item
 * @returns {boolean}
 */
export function isObject(item: any): boolean {
  return (item && typeof item === 'object' && !Array.isArray(item));
}

/**
 * Deep merge two objects.
 * @param target
 * @param sources
 */
export function mergeDeep<T extends object = any>(target: Partial<T>, ...sources: Partial<T>[]): Partial<T> {
  if (!sources.length) {
    return target;
  }
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) {
          Object.assign(target, { [key]: {} });
        }
        mergeDeep((target as any)[key], (source as any)[key]);
      }
      else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return mergeDeep<T>(target, ...sources);
}

export function pagedRequestToQueryParams(pagedRequest: PagedRequest): HttpParams {
  let params = new HttpParams();
  Object.keys(pagedRequest).forEach(
    (key) => {
      const keyValue = (pagedRequest as any)[key];
      if (keyValue instanceof Array) {
        keyValue.forEach((val) => {
          params = params.append(key, val);
        });
      }
      else if (keyValue !== undefined && keyValue !== null && keyValue !== '') {
        params = params.append(key, '' + keyValue);
      }
    }
  );
  return params;
}

export function round(nmbr: number, decimalCount: number): number {
  return Math.round(nmbr * (10 ** decimalCount)) / (10 ** decimalCount);
}

export function dateInPast(date: string | DateTime): boolean {
  return (typeof date === 'string' ? DateTime.fromISO(date) : date) < DateTime.now();
}

export function dateInFuture(date: string | DateTime): boolean {
  return (typeof date === 'string' ? DateTime.fromISO(date) : date) > DateTime.now();
}

export function dateBetween(dateStart: string | DateTime, dateEnd: string | DateTime): boolean {
  return dateInPast(dateStart) && dateInFuture(dateEnd);
}

/**
 * use this, if you want to have a fallback for an observable
 * When the first observable fails, the next one gets executed, when the next one fails we will continue with the next one etc.
 * When we have a successful output we will emit the result and stop executing the remaining fallbacks
 * You will get the result of the first successful request.
 * @param {Observable<any>[]} observables
 * @returns {Observable<any>}
 */
export function observableWithFallbacks<T>(observables: Observable<T>[]): Observable<T> {
  if (observables.length === 0) {
    throw Error('No remaining fallback-languages to try.');
  }
  // Recursive case: execute the first request and move on to the next one if it fails
  const [firstRequest, ...remainingRequests] = observables;
  return firstRequest
    // If the request fails, move on to the next request
    .pipe(catchError(() => observableWithFallbacks(remainingRequests)));
}
